import { ref } from 'vue'

export type OrphanedMediaAsset = Omit<OrphanedMediaOption, 'status' | 'deleteMedia'>

// Assets a save left unused, waiting for the editor to decide whether to delete them
// (rendered by the "unused assets" popup in layouts/default.vue).
const promptOpen = ref(false)
const promptMedia = ref<OrphanedMediaOption[]>([])
const promptError = ref('')

export function useOrphanedMedia() {
    async function delete_orphaned_media(media: OrphanedMediaAsset) {
        // b-media items are just pointers at the real asset in b-image/b-video (see
        // types/MediaBucket.ts) — their id isn't a b-image/b-video contentId, so they go through
        // the generic bucket delete instead, which resolves the pointer and cascades the deletion.
        if (media.bucket === 'b-media') {
            const response = await fetch(`/api/bucketContent/media/${ encodeURIComponent(media.id) }`, {
                method: 'DELETE',
                credentials: 'include',
                headers: useRequestHeaders(['cookie'])
            })
            const payload = await response.json().catch(() => null)
            if (!response.ok || !payload?.success || !payload.data?.deleted) {
                throw new Error(payload?.message ?? `Could not delete ${ media.label }`)
            }
            return
        }

        // Files/audio have no derived variants to clean up, so their delete endpoints respond with a
        // single plain JSON payload rather than an SSE progress stream like image/video.
        if (media.type === 'file' || media.type === 'audio') {
            const endpoint = media.type === 'file'
                    ? `/api/files/file/${ encodeURIComponent(media.id) }`
                    : `/api/files/audio/${ encodeURIComponent(media.id) }`
            const response = await fetch(endpoint, {
                method: 'DELETE',
                credentials: 'include',
                headers: useRequestHeaders(['cookie'])
            })
            const payload = await response.json().catch(() => null)
            if (!response.ok || !payload?.success || !payload.data?.deleted) {
                throw new Error(payload?.message ?? `Could not delete ${ media.label }`)
            }
            return
        }

        const endpoint = media.type === 'image'
                ? `/api/files/image/${ encodeURIComponent(media.id) }`
                : `/api/files/video/${ encodeURIComponent(media.id) }`
        const response = await fetch(endpoint, {
            method: 'DELETE',
            credentials: 'include',
            headers: useRequestHeaders(['cookie'])
        })
        if (!response.ok || !response.body) throw new Error(`Could not delete ${ media.label }`)

        const body = await response.text()
        const events = body
                .split(/\r?\n/)
                .filter(line => line.startsWith('data:'))
                .map(line => JSON.parse(line.slice(5).trim()))
        const error = events.find(event => event.type === 'error')
        if (error) throw new Error(error.message ?? `Could not delete ${ media.label }`)
        if (!events.some(event => event.type === 'complete')) {
            throw new Error(`Could not delete ${ media.label }`)
        }
    }

    function prompt_orphaned_media(media: OrphanedMediaAsset[]) {
        if (!media.length) return
        promptMedia.value = media.map(item => ({
            ...item,
            status: 'available',
            deleteMedia: () => delete_orphaned_media(item),
        }))
        promptError.value = ''
        promptOpen.value = true
    }

    async function delete_prompted_media(media: OrphanedMediaOption) {
        if (media.status === 'deleting' || media.status === 'deleted') return
        media.status = 'deleting'
        try {
            await media.deleteMedia()
            media.status = 'deleted'
        } catch (error) {
            media.status = 'error'
            promptError.value = error instanceof Error ? error.message : String(error)
        }
    }

    async function delete_all_prompted_media() {
        promptError.value = ''
        // Sequential: image/video deletes stream progress and are heavy on the server.
        for (const media of promptMedia.value) await delete_prompted_media(media)
    }

    return {
        delete_orphaned_media,
        prompt_orphaned_media,
        delete_prompted_media,
        delete_all_prompted_media,
        promptOpen,
        promptMedia,
        promptError,
    }
}
